import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { AdminTableIconComponent } from './table-icon.component';
import { clampPage, pageCount, pageReport, pageWindow, TablePage } from './table.util';

/** Page links, first/prev/next/last, rows-per-page select and the "Showing x to y" line. */
@Component({
  selector: 'admin-paginator',
  standalone: true,
  imports: [AdminTableIconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (itemLabel) {
      <span class="current">{{ report }}</span>
    }
    <button type="button" aria-label="First Page" [disabled]="page <= 1" (click)="go(1)">
      <admin-table-icon name="angleDoubleLeft"></admin-table-icon>
    </button>
    <button type="button" aria-label="Previous Page" [disabled]="page <= 1" (click)="go(page - 1)">
      <admin-table-icon name="angleLeft"></admin-table-icon>
    </button>
    <span class="pages">
      @for (p of links; track p) {
        <button
          type="button"
          [class.selected]="p === page"
          [attr.aria-current]="p === page ? 'page' : null"
          [attr.aria-label]="p"
          (click)="go(p)"
        >
          {{ p }}
        </button>
      }
    </span>
    <button type="button" aria-label="Next Page" [disabled]="page >= pages" (click)="go(page + 1)">
      <admin-table-icon name="angleRight"></admin-table-icon>
    </button>
    <button type="button" aria-label="Last Page" [disabled]="page >= pages" (click)="go(pages)">
      <admin-table-icon name="angleDoubleRight"></admin-table-icon>
    </button>
    @if (pageSizeOptions.length) {
      <span class="rpp">
        <select aria-label="Rows per page" [value]="pageSize" (change)="resize($any($event.target).value)">
          @for (size of pageSizeOptions; track size) {
            <option [value]="size" [selected]="size === pageSize">{{ size }}</option>
          }
        </select>
        <admin-table-icon name="chevronDown"></admin-table-icon>
      </span>
    }
  `,
  styles: [
    `
      :host {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: center;
        gap: 4px;
        padding: 10px 16px;
        background: #fff;
        color: var(--cnt-ink, #222);
        font-size: 0.875rem;
      }
      .current {
        margin-right: 8px;
        color: var(--cnt-muted, #707070);
        font-size: 0.8125rem;
      }
      .pages {
        display: flex;
        align-items: center;
        gap: 4px;
      }
      button {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 36px;
        min-width: 36px;
        height: 36px;
        padding: 0;
        border: 1px solid transparent;
        border-radius: 9999px;
        background: transparent;
        color: var(--cnt-ink, #222);
        font: inherit;
        font-weight: 700;
        line-height: 16px;
        cursor: pointer;
        transition:
          background 0.2s,
          color 0.2s,
          border-color 0.2s;
      }
      button:not(:disabled):not(.selected):hover {
        border-color: var(--cnt-line-strong, rgba(34, 34, 34, 0.15));
        color: var(--cnt-trinidad, #e3530d);
      }
      button:disabled {
        opacity: 0.35;
        cursor: default;
      }
      button.selected {
        background: var(--cnt-trinidad, #e3530d);
        color: #fff;
      }
      button:focus-visible,
      select:focus-visible {
        outline: 2px solid var(--cnt-green, #295d42);
        outline-offset: 2px;
      }
      button admin-table-icon {
        width: 16px;
        height: 16px;
      }
      .rpp {
        position: relative;
        display: inline-flex;
        margin-left: 8px;
        border: 1px solid var(--cnt-line-strong, rgba(34, 34, 34, 0.15));
        border-radius: 9999px;
        background: rgba(247, 245, 236, 0.6);
      }
      .rpp:hover {
        border-color: var(--cnt-trinidad, #e3530d);
      }
      select {
        appearance: none;
        field-sizing: content;
        border: 0;
        background: transparent;
        color: var(--cnt-ink, #222);
        font: inherit;
        line-height: 20px;
        padding: 8px 40px 8px 14px;
        border-radius: 9999px;
        cursor: pointer;
      }
      .rpp admin-table-icon {
        position: absolute;
        right: 12px;
        top: 50%;
        width: 16px;
        height: 16px;
        margin-top: -8px;
        color: var(--cnt-muted, #707070);
        pointer-events: none;
      }
    `,
  ],
})
export class AdminPaginatorComponent {
  /** 1-based. */
  @Input() page = 1;
  @Input() pageSize = 50;
  @Input() total = 0;
  @Input() pageSizeOptions: number[] = [];
  /** Noun for the report line ("users"); no report line when empty. */
  @Input() itemLabel = '';
  @Output() readonly pageChange = new EventEmitter<TablePage>();

  /** 0 when there are no rows, so no page links show (as in PrimeNG). */
  get pages(): number {
    return this.total > 0 ? pageCount(this.total, this.pageSize) : 0;
  }

  get links(): number[] {
    return pageWindow(this.page, this.pages);
  }

  get report(): string {
    return pageReport(this.page, this.pageSize, this.total, this.itemLabel);
  }

  go(page: number): void {
    const next = clampPage(page, this.total, this.pageSize);
    if (next !== this.page) this.pageChange.emit({ page: next, pageSize: this.pageSize });
  }

  resize(value: string): void {
    const pageSize = Number(value);
    if (pageSize && pageSize !== this.pageSize) this.pageChange.emit({ page: 1, pageSize });
  }
}
